import { createServerFn } from "@tanstack/react-start";
import { getRequest } from "@tanstack/react-start/server";

type LatLng = { lat: number; lng: number } | null;

// Module-level cache (per worker instance). Avoids hammering the geocoder.
const cache = new Map<string, LatLng>();

const ALLOWED_HOSTS = new Set([
  "brulo.fit",
  "www.brulo.fit",
  "brulo.lovable.app",
]);

function isAllowedOrigin(originHeader: string | null, requestUrl: string): boolean {
  if (!originHeader) return false;
  let originHost: string;
  try {
    originHost = new URL(originHeader).host;
  } catch {
    return false;
  }
  // Same-origin: origin host matches the request host.
  try {
    const reqHost = new URL(requestUrl).host;
    if (originHost === reqHost) return true;
  } catch {
    // ignore
  }
  if (ALLOWED_HOSTS.has(originHost)) return true;
  // Allow lovable preview subdomains for this project.
  if (/^id-preview--[a-z0-9-]+\.lovable\.app$/i.test(originHost)) return true;
  if (/^project--[a-z0-9-]+(-dev)?\.lovable\.app$/i.test(originHost)) return true;
  return false;
}

export const geocodeAddresses = createServerFn({ method: "POST" })
  .inputValidator((data: { addresses: string[] }) => {
    if (!data || !Array.isArray(data.addresses)) {
      return { addresses: [] as string[] };
    }
    return {
      addresses: data.addresses
        .filter((a) => typeof a === "string" && a.trim().length > 0)
        .slice(0, 10)
        .map((a) => a.trim().slice(0, 250)),
    };
  })
  .handler(async ({ data }) => {
    // Anti-abuse: only allow calls that originate from a trusted browser
    // context (same-origin or a known Brulo host). This blocks the endpoint
    // from being repurposed as a free public geocoding proxy while still
    // supporting unauthenticated flows like signup and the public map.
    const request = getRequest();
    const originHeader =
      request.headers.get("origin") ?? request.headers.get("referer");
    if (!isAllowedOrigin(originHeader, request.url)) {
      throw new Error("Forbidden");
    }

    const LOVABLE_API_KEY = process.env.LOVABLE_API_KEY;
    const GOOGLE_MAPS_API_KEY = process.env.GOOGLE_MAPS_API_KEY;
    if (!LOVABLE_API_KEY) throw new Error("LOVABLE_API_KEY is not configured");
    if (!GOOGLE_MAPS_API_KEY) throw new Error("GOOGLE_MAPS_API_KEY is not configured");

    const GATEWAY = "https://connector-gateway.lovable.dev/google_maps";
    const results: Record<string, LatLng> = {};

    await Promise.all(
      data.addresses.map(async (addr) => {
        if (cache.has(addr)) {
          results[addr] = cache.get(addr)!;
          return;
        }
        try {
          const hasCountry = /m[ée]xico/i.test(addr);
          const fullAddr = hasCountry ? addr : `${addr}, Aguascalientes, México`;
          const q = encodeURIComponent(fullAddr);
          const res = await fetch(`${GATEWAY}/maps/api/geocode/json?address=${q}`, {
            headers: {
              Authorization: `Bearer ${LOVABLE_API_KEY}`,
              "X-Connection-Api-Key": GOOGLE_MAPS_API_KEY,
            },
          });
          const json = (await res.json()) as {
            results?: { geometry?: { location?: { lat: number; lng: number } } }[];
          };
          const loc = json?.results?.[0]?.geometry?.location ?? null;
          const val: LatLng = loc ? { lat: loc.lat, lng: loc.lng } : null;
          cache.set(addr, val);
          results[addr] = val;
        } catch {
          results[addr] = null;
        }
      }),
    );

    return { results };
  });
